import { FEEL } from './FeelConstants';

/* ═══════════════════════════════════════════════════════════════════════════
   LES TROIS BOMBES (PLAN.md §6.3).

   Une seule bombe s'emporte par run. Ces trois entrées ne sont donc pas trois
   objets à cumuler, ce sont trois RÉPONSES possibles à la même touche.

   ── LA RÈGLE QUI NE SE NÉGOCIE PAS ──

   ⚠️ La bombe est une SORTIE DE SECOURS. Elle doit TOUJOURS sauver, jamais
   « peut-être sauver ». Un joueur qui doute cesse de s'en servir, et le
   mécanisme entier — y compris la fenêtre de death-bomb, qui est le rattrapage
   le plus gratifiant du genre — devient décoratif.

   D'où le partage retenu ici, et il est structurel :

     · l'EFFACEMENT des projectiles alentour (`FEEL.bombClearRadius`, 900 u) est
       IDENTIQUE pour les trois. C'est le contrat, il n'est pas un paramètre.
       900 u couvre la quasi-totalité d'un terrain de 900 × 1 200 : après une
       bombe, quelle qu'elle soit, l'écran est vide autour du joueur.
     · ce qui VARIE est la durée d'invulnérabilité et l'effet de bord — c'est
       exactement ce qu'annonce le commentaire de l'arbre : « les trois ne
       diffèrent pas en puissance mais en durée d'invulnérabilité et en effet de
       bord ».

   Le texte du nœud TEMPS MORT dit qu'elle « ne sauve pas ». La suite du même
   commentaire lève l'ambiguïté — « elle ne DÉTRUIT rien ». C'est un défaut de
   dégâts, pas un défaut de sauvetage : elle purge et immunise comme les deux
   autres, et un peu mieux, puisqu'elle ralentit ce qui revient ensuite.

   ── POURQUOI CE SONT DES FORMES DE MOTEUR ET NON DU CONTENU ──

   Contrairement aux ennemis ou aux armes secondaires, une bombe ne se décrit
   pas par des paramètres d'une forme commune : la colonne du BRISANT inflige
   des dégâts dans le temps, le TEMPS MORT dilate l'intégration des projectiles
   ennemis. Ce sont deux CAPACITÉS distinctes du moteur, pas deux réglages.
   Le catalogue est d'ailleurs fermé par le plan — trois bombes, pas trente.

   Les identifiants, les noms et les textes du joueur vivent, eux, dans
   `src/content/bombs/` : le moteur n'a pas à connaître de chaîne.
   ═══════════════════════════════════════════════════════════════════════════ */

export const Bomb = {
  /** La bombe de départ, offerte. La plus longue invulnérabilité, aucun effet
   *  de bord — c'est la référence à laquelle les deux autres s'échangent. */
  Eclat: 0,
  /** Moitié moins d'invulnérabilité, en échange de dégâts de boss. */
  Brisant: 1,
  /** Ne détruit rien, ralentit tout, et fait payer le frôlement plus cher. */
  TempsMort: 2,
} as const;

export type BombId = (typeof Bomb)[keyof typeof Bomb];

export const BOMB_COUNT = 3;

export interface BombSpec {
  /** Invulnérabilité accordée au déclenchement, en secondes. */
  readonly iframes: number;
  /** Durée de l'effet de bord, en secondes. 0 = aucun effet de bord. */
  readonly fx: number;
  /** DEMI-largeur de la colonne de dégâts, en unités (BRISANT). */
  readonly columnHalf: number;
  /** Dégâts TOTAUX infligés par la colonne à chaque ennemi qui y séjourne
   *  pendant toute la durée (BRISANT). */
  readonly columnDamage: number;
  /** Facteur de vitesse appliqué aux projectiles ennemis pendant `fx`
   *  (TEMPS MORT). 1 = aucun ralentissement. */
  readonly bulletScale: number;
  /** Facteur appliqué à la valeur d'un frôlement pendant `fx` (TEMPS MORT). */
  readonly grazeMul: number;
}

/* Les chiffres viennent MOT POUR MOT des textes de l'arbre (tree.def.ts) : ce
   sont eux que le joueur lit avant de payer, et un écart entre le prix affiché
   et la marchandise livrée est la seule erreur qu'aucun équilibrage ne rattrape.

     BRISANT     « 0,90 s d'invulnérabilité, colonne de 260 u,
                   3 000 dégâts sur 2,2 s »
     TEMPS MORT  « 1,20 s d'invulnérabilité, balles à 25 % de vitesse pendant
                   3,5 s, +50 % de valeur de frôlement »

   La colonne est donnée en LARGEUR totale (260 u) ; on stocke la demi-largeur,
   qui est ce que la comparaison de position demande — convertir à chaque tick
   dans la boucle serait une division par deux répétée pour rien. */
export const BOMB_SPECS: readonly BombSpec[] = [
  {
    iframes: FEEL.bombIframes,
    fx: 0,
    columnHalf: 0,
    columnDamage: 0,
    bulletScale: 1,
    grazeMul: 1,
  },
  {
    iframes: 0.9,
    fx: 2.2,
    columnHalf: 130,
    columnDamage: 3000,
    bulletScale: 1,
    grazeMul: 1,
  },
  {
    iframes: 1.2,
    fx: 3.5,
    columnHalf: 0,
    columnDamage: 0,
    bulletScale: 0.25,
    grazeMul: 1.5,
  },
];

/** Ramène n'importe quelle entrée à une bombe existante.
 *
 *  Même rôle que `clampDifficulty` : une sauvegarde d'une version antérieure ou
 *  un index hors bornes donnerait `undefined` dans la table, donc des i-frames
 *  `NaN`, donc une bombe qui n'immunise plus. La panne serait silencieuse et le
 *  jeu resterait parfaitement jouable — jusqu'à la première bombe. */
export function clampBomb(kind: number): number {
  if (!(kind >= 0)) return Bomb.Eclat; // couvre aussi NaN
  const i = Math.floor(kind);
  return i < BOMB_COUNT ? i : Bomb.Eclat;
}

export function bombSpec(kind: number): BombSpec {
  return BOMB_SPECS[clampBomb(kind)]!;
}

/**
 * Facteur de vitesse des projectiles ennemis à cet instant.
 *
 * Renvoie 1 hors effet : l'appelant multiplie sans branche, et le cas neutre ne
 * peut pas être oublié.
 *
 * ⚠️ Le ralentissement s'applique à l'INTÉGRATION, jamais aux vitesses
 * stockées. Écrire 0,25 × vx dans le pool au déclenchement aurait deux défauts
 * qui se voient en jeu : les projectiles nés pendant la fenêtre resteraient à
 * pleine vitesse — donc un rideau à deux régimes que rien n'explique — et il
 * faudrait restaurer les vitesses à l'expiration, c'est-à-dire retrouver des
 * slots qui auront été recyclés entre-temps.
 */
export function enemyBulletTimeScale(kind: number, fxT: number): number {
  if (fxT <= 0) return 1;
  return bombSpec(kind).bulletScale;
}

/** Facteur appliqué à la valeur d'un frôlement à cet instant. Renvoie 1 hors
 *  effet, pour la même raison. */
export function grazeValueMul(kind: number, fxT: number): number {
  if (fxT <= 0) return 1;
  return bombSpec(kind).grazeMul;
}

/** Dégâts de colonne à appliquer sur UN tick, par ennemi présent.
 *
 *  Répartition uniforme : un ennemi qui entre dans la colonne à mi-parcours
 *  prend la moitié du total, ce qui est la seule lecture cohérente avec le mot
 *  « colonne » — une zone qui brûle, pas une détonation différée. */
export function columnDamagePerTick(kind: number, dt: number): number {
  const spec = bombSpec(kind);
  if (spec.columnDamage <= 0 || spec.fx <= 0) return 0;
  return (spec.columnDamage / spec.fx) * dt;
}
